import { generateClient } from "aws-amplify/data";
import type { Schema } from "amplify/data/resource";
import type {
  RetrySyncOutcome,
  RetrySyncResult,
  SupportTicketRow,
  SupportTicketStatus,
  SupportFilters,
} from "./types";
import { SUPPORT_SELECTION_SET } from "./types";

const client = generateClient<Schema>();

// ── Helpers ─────────────────────────────────────────────────────────────────

function mapToRow(raw: unknown): SupportTicketRow {
  const t = raw as {
    id: string;
    reference?: string | null;
    topic?: string | null;
    status?: string | null;
    businessId?: string | null;
    userId?: string | null;
    createdAt?: string | null;
  };
  return {
    id: t.id,
    reference: t.reference ?? t.id,
    topic: t.topic ?? "other",
    status: (t.status as SupportTicketStatus | null) ?? "Pending",
    businessId: t.businessId ?? null,
    userId: t.userId ?? "",
    createdAt: t.createdAt ?? null,
  };
}

function byCreatedAtDesc(a: SupportTicketRow, b: SupportTicketRow): number {
  return (b.createdAt ?? "").localeCompare(a.createdAt ?? "");
}

function buildAmplifyFilter(
  filters: SupportFilters,
): Record<string, unknown> | undefined {
  if (!filters.searchQuery.trim()) return undefined;
  return { reference: { contains: filters.searchQuery.trim() } };
}

// ── Repository ────────────────────────────────────────────────────────────────

export interface ListAllSupportOpts {
  filters: SupportFilters;
}

export class SupportRequestsRepository {
  /**
   * Fetches every support ticket matching the server-side filters by
   * paginating through all pages, then sorts newest-first in memory. Status
   * and topic are filtered client-side by the BLoC. Support volume is low, so
   * a scan with a lightweight selection set is comfortably fast.
   *
   * A `businessId` filter goes through that index rather than a filtered
   * scan: scoping to one merchant is the common case from the merchant page,
   * and scanning the whole table to discard all but one merchant's rows gets
   * slower every week.
   */
  async listAll(opts: ListAllSupportOpts): Promise<SupportTicketRow[]> {
    const businessId = opts.filters.businessId.trim();
    const filter = buildAmplifyFilter(opts.filters);
    const allItems: SupportTicketRow[] = [];
    let cursor: string | undefined;

    do {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Amplify ModelFilter is too deeply complex to type-safely construct
      const listOpts: any = {
        limit: 500,
        nextToken: cursor,
        selectionSet: [...SUPPORT_SELECTION_SET],
        filter,
      };
      const { data, nextToken, errors } = businessId
        ? await client.models.SupportTicket.supportTicketsByBusinessId(
            { businessId },
            listOpts,
          )
        : await client.models.SupportTicket.list(listOpts);
      if (errors?.length) throw new Error(errors[0].message);

      allItems.push(...(data ?? []).map(mapToRow));
      cursor = nextToken ?? undefined;
    } while (cursor);

    allItems.sort(byCreatedAtDesc);
    return allItems;
  }

  /**
   * Lists a single merchant's tickets via the businessId secondary index,
   * paginating through all pages so merchants with high ticket volume return
   * complete results. Sorted newest-first in memory.
   */
  async listByBusiness(businessId: string): Promise<SupportTicketRow[]> {
    const allItems: SupportTicketRow[] = [];
    let cursor: string | undefined;

    do {
      const { data, nextToken, errors } =
        await client.models.SupportTicket.supportTicketsByBusinessId(
          { businessId },
          {
            limit: 500,
            nextToken: cursor,
            selectionSet: [...SUPPORT_SELECTION_SET],
          },
        );
      if (errors?.length) throw new Error(errors[0].message);

      allItems.push(...(data ?? []).map(mapToRow));
      cursor = nextToken ?? undefined;
    } while (cursor);

    return allItems.sort(byCreatedAtDesc);
  }

  /**
   * Re-runs a single request's HubSpot sync.
   *
   * The ticket stream is insert-filtered, so no write to the row can trigger
   * another delivery — this mutation is the only way one failed request gets
   * another attempt. Safe to press repeatedly: the sync resumes from whatever
   * HubSpot ids a partial attempt persisted rather than filing a duplicate.
   *
   * Every outcome comes back as data, including a repeat failure; only a broken
   * mutation throws.
   */
  async retrySync(ticketId: string): Promise<RetrySyncResult> {
    const { data, errors } = await client.mutations.retrySupportTicketSync({
      ticketId,
    });
    if (errors?.length) throw new Error(errors[0].message);
    if (!data) throw new Error("Empty response from retrySupportTicketSync");

    return {
      outcome: data.outcome as RetrySyncOutcome,
      hubspotTicketId: data.hubspotTicketId ?? null,
      message: data.message ?? null,
      retryable: data.retryable ?? null,
    };
  }
}
